import React from 'react'
import type { Billing, CardAccount, PaidRung } from '../api/client'
import { useApp } from '../context/AppContext'
import { forgetPending, pendingRung } from '../billing/pending'
import { SET_UP_INCLUDED, rungName, youreOn } from '../billing/rungs'
import { useCardAccount } from '../payments/useCardAccount'
import { pollMe, reached } from '../billing/useBilling'
import AuthLayout, { AuthLogo } from './AuthLayout'

// The path Stripe Checkout sends her back to (the API's success URL).
export const CONFIRMING_PATH = /^\/settings\/plan\/confirming\/?$/

type Phase = 'waiting' | 'done' | 'late'

// doneLine is what she has now. Payments bought before Stripe has approved
// her card account is hers, but card payments start once it is. Pro bought
// before any Stripe setup says that card payments come with it and where
// to set them up: the next step, not a surprise later.
function doneLine(want: PaidRung, billing: Billing | undefined, account: CardAccount | null): { title: string; detail?: string } {
  if (want === 'card_payments' && billing && !billing.entitlements.card_payments) {
    return { title: `You’re on ${rungName('card_payments')}`, detail: 'Card payments start once Stripe has approved your details.' }
  }
  if (want === 'pro' && account?.available && account.status === 'none') {
    return { title: youreOn(want), detail: SET_UP_INCLUDED }
  }
  return { title: youreOn(want) }
}

// Confirming is where Stripe Checkout returns her (spec 1.1). Stripe tells
// Harmovi by webhook, so the page asks GET /v1/me every two seconds, for up
// to a minute, until the rung she paid for is on. After that the payment has
// still gone through; the nightly reconcile backs the promise that the app
// catches up.
export default function Confirming({ onDone }: { onDone: () => void }) {
  const { state, dispatch } = useApp()
  const account = useCardAccount()
  const [want] = React.useState<PaidRung>(() => pendingRung() ?? 'card_payments')
  const [phase, setPhase] = React.useState<Phase>('waiting')

  React.useEffect(() => {
    let stopped = false
    void pollMe(
      (me) => dispatch({ type: 'SESSION_STARTED', payload: me }),
      (me) => reached(me.billing, want),
      () => stopped,
    ).then((ok) => {
      if (stopped) return
      setPhase(ok ? 'done' : 'late')
      if (ok) forgetPending()
    })
    return () => {
      stopped = true
    }
  }, [dispatch, want])

  const done = phase === 'done' ? doneLine(want, state.me?.billing, account) : null
  return (
    <AuthLayout>
      <div className="auth-form" aria-live="polite">
        <AuthLogo />
        <div className="auth-branding">
          {phase === 'waiting' && (
            <>
              <h1>Confirming your payment…</h1>
              <p className="auth-subtitle">This takes a few seconds.</p>
            </>
          )}
          {done && (
            <>
              <h1>{done.title}</h1>
              {done.detail && <p className="auth-subtitle">{done.detail}</p>}
            </>
          )}
          {phase === 'late' && (
            <>
              <h1>Payment received</h1>
              {/* So she doesn't pay twice while the webhook is late (review, 2026-10-25). */}
              <p className="auth-subtitle">Your payment went through. {rungName(want)} can take a few minutes to show here; you don’t need to pay again.</p>
            </>
          )}
        </div>
        {phase !== 'waiting' && (
          <button type="button" className="btn btn-primary" onClick={onDone}>
            Back to Harmovi
          </button>
        )}
      </div>
    </AuthLayout>
  )
}
